import { gql, useMutation, useQuery } from '@apollo/client';
import IconBookmark from '@apollo/icons/default/IconBookmark.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import IconSandbox from '@apollo/icons/default/IconSandbox.svg';
import {
  Alert,
  Avatar,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { ListItem } from '@apollo/space-kit/ListItem';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { orderBy } from 'lodash';
import React from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import * as Yup from 'yup';

import {
  graphRefToString,
  parseGraphRef,
  useGraphRef,
} from 'src/app/graph/hooks/useGraphRef';
import {
  explorerRouteConfig,
  sandboxExplorerRouteConfig,
  sandboxGraphRouteConfig,
} from 'src/app/graph/routes';
import { GraphSelect } from 'src/app/graph/sandboxShared/GraphSelect';
import { MultiOrgSelect } from 'src/app/graph/sandboxShared/MultiOrgSelect';
import { SandboxGraphRefSelectorsUserFragment } from 'src/app/graph/sandboxShared/SandboxGraphRefSelectors';
import { VariantSelect } from 'src/app/graph/sandboxShared/VariantSelect';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { createUniqueName } from 'src/lib/createUniqueName';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { mergeQueryParams, setOverlayClosedLocation } from 'src/lib/routing';

import {
  UseCollectionNameValidationCollectionFragment,
  useCollectionNameValidation,
} from '../sharedValidation';

const SANDBOX_ID = '__sandbox__';

export const DuplicateCollectionModal = ({
  isOpen,
  collectionId,
}: {
  isOpen: boolean;
  collectionId: string;
}) => {
  const { data, loading } = useQuery<
    GraphQLTypes.DuplicateCollectionModalQuery,
    GraphQLTypes.DuplicateCollectionModalQueryVariables
  >(
    gql`
      query DuplicateCollectionModalQuery($collectionId: ID!) {
        operationCollection(id: $collectionId) {
          ... on OperationCollection {
            id
            name
            isShared
            variants {
              id
              graph {
                id
                account {
                  id
                }
              }
            }
            ...UseCollectionNameValidationCollectionFragment
          }
        }
        me {
          id
          ...SandboxGraphRefSelectorsUserFragment
        }
      }

      ${SandboxGraphRefSelectorsUserFragment}
      ${UseCollectionNameValidationCollectionFragment}
    `,
    {
      variables: {
        collectionId,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const collection =
    data?.operationCollection.__typename === 'OperationCollection'
      ? data.operationCollection
      : undefined;
  const user = data?.me?.__typename === 'User' ? data.me : undefined;

  const [duplicateCollection] = useMutation<
    GraphQLTypes.DuplicateCollection,
    GraphQLTypes.DuplicateCollectionVariables
  >(
    gql`
      mutation DuplicateCollection(
        $collectionId: ID!
        $collectionName: String!
        $isSandbox: Boolean!
        $isShared: Boolean!
        $variantRef: ID
      ) {
        operationCollection(id: $collectionId) {
          duplicateCollection(
            isSandbox: $isSandbox
            isShared: $isShared
            name: $collectionName
            variantRef: $variantRef
          ) {
            ... on OperationCollection {
              id
              isSandbox
              variants {
                id
                operationCollections {
                  id
                }
              }
              createdBy @include(if: $isSandbox) {
                id
                ... on User {
                  sandboxOperationCollections {
                    id
                  }
                }
              }
            }
            ... on Error {
              message
            }
          }
        }
      }
    `,
    {
      onError: (error) => setGlobalErrorMessage(error.message),
    },
  );

  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;
  const currentGraphRef = useGraphRef();

  const [globalErrorMessage, setGlobalErrorMessage] = React.useState<string>();

  const {
    collectionNameValidation,
    existingCollectionNames,
    loading: collectionNameValidationLoading,
  } = useCollectionNameValidation({ collection, loading });
  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        name: collectionNameValidation,
        selectedOrgId: Yup.string().required('Select an org, or sandbox'),
        selectedGraphId: Yup.string().when('selectedOrgId', (selectedOrgId) => {
          if (selectedOrgId !== SANDBOX_ID) {
            return Yup.string().required('Select a graph id');
          }
        }),
        selectedVariantId: Yup.string().when(
          'selectedOrgId',
          (selectedOrgId) => {
            if (selectedOrgId !== SANDBOX_ID) {
              return Yup.string().required('Select a variant');
            }
          },
        ),
        isShared: Yup.bool().required(),
      }),
    [collectionNameValidation],
  );

  const initialValues = React.useMemo((): Yup.InferType<
    typeof validationSchema
  > => {
    const variant = collection?.variants[0];
    const graph = variant?.graph;
    const org = graph?.account;
    return {
      name: createUniqueName(
        collection?.name ? `Copy of ${collection.name}` : '',
        existingCollectionNames,
      ),
      selectedOrgId: org ? org.id : '',
      selectedGraphId: org && graph ? graph.id : '',
      selectedVariantId: org && graph && variant ? variant.id : '',
      isShared: false,
    };
  }, [collection, existingCollectionNames]);

  const trackCustomEvent = useTrackCustomEvent();

  const location = useLocation();
  const history = useHistory();
  const onClose = React.useCallback(
    (focusCollectionId?: string) => {
      const modalClosedLocation = setOverlayClosedLocation(location);
      if (focusCollectionId) {
        history.replace({
          ...modalClosedLocation,
          search: mergeQueryParams(modalClosedLocation.search, {
            focusCollectionId,
          }),
        });
      } else {
        history.replace(modalClosedLocation);
      }
    },
    [location, history],
  );

  const {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    async onSubmit(formValues) {
      try {
        const isSandbox = formValues.selectedOrgId === SANDBOX_ID;
        const isShared = !isSandbox && formValues.isShared;
        const result = (
          await duplicateCollection({
            variables: {
              collectionId,
              collectionName: formValues.name,
              isShared,
              isSandbox,
              variantRef: isSandbox ? null : formValues.selectedVariantId,
            },
          })
        ).data?.operationCollection?.duplicateCollection;
        if (result?.__typename === 'OperationCollection') {
          trackCustomEvent({
            action: 'duplicate_operation_collection',
            category: 'Operation Collections',
            label: isSandbox ? 'sandbox' : isShared ? 'shared' : 'personal',
          });
          if (isSandbox) {
            if (!isSandboxRoute) {
              window.open(
                // eslint-disable-next-line local/no-direct-route-config-access
                sandboxExplorerRouteConfig.path({
                  focusCollectionId: result.id,
                }),
                '_blank',
              );
            }
          } else {
            const selectedGraphRef = parseGraphRef(
              formValues.selectedVariantId,
            );
            // Selected graph ref different than current graph ref
            if (
              selectedGraphRef &&
              graphRefToString(currentGraphRef) !==
                graphRefToString(selectedGraphRef)
            ) {
              window.open(
                // eslint-disable-next-line local/no-direct-route-config-access
                explorerRouteConfig.path({
                  ...selectedGraphRef,
                  focusCollectionId: result.id,
                }),
                '_blank',
              );
            }
          }
          onClose(result.id);
        } else {
          setGlobalErrorMessage(
            result?.message ?? 'Failed to duplicate collection',
          );
        }
      } catch {
        // Surfaced through `globalErrorMessage`
      }
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const orgs = React.useMemo(
    () => user?.memberships.map((membership) => membership.account) ?? [],
    [user],
  );
  const selectedOrg = values.selectedOrgId
    ? orgs?.find((org) => org.id === values.selectedOrgId)
    : undefined;
  const graphs = React.useMemo(
    () =>
      selectedOrg?.services
        ? orderBy(selectedOrg.services, () => 1)
        : undefined,
    [selectedOrg],
  );
  const selectedGraph = values.selectedGraphId
    ? selectedOrg?.services.find((graph) => graph.id === values.selectedGraphId)
    : undefined;
  const variants = React.useMemo(
    () =>
      selectedGraph?.variants
        ? orderBy(selectedGraph.variants, () => 1)
        : undefined,
    [selectedGraph],
  );
  const selectedVariant = values.selectedVariantId
    ? selectedGraph?.variants.find(
        (variant) => variant.id === values.selectedVariantId,
      )
    : undefined;

  const setSelectedOrgId = React.useCallback(
    (newOrgId: string) => {
      setFieldValue('selectedOrgId', newOrgId);
      setFieldValue('selectedGraphId', '');
      setFieldValue('selectedVariantId', '');
      setFieldValue('isShared', false);
    },
    [setFieldValue],
  );
  const setSelectedGraphId = React.useCallback(
    (newGraphId: string) => {
      setFieldValue('selectedGraphId', newGraphId);
      setFieldValue('selectedVariantId', '');
    },
    [setFieldValue],
  );
  const setSelectedVariantId = React.useCallback(
    (newVariantId: string) => setFieldValue('selectedVariantId', newVariantId),
    [setFieldValue],
  );
  const setIsShared = React.useCallback(
    (newIsShared: boolean) => setFieldValue('isShared', newIsShared),
    [setFieldValue],
  );

  // If there is a single graph, select it
  React.useEffect(() => {
    if (selectedOrg?.services?.length === 1) {
      setSelectedGraphId(selectedOrg.services[0]?.id ?? '');
    }
  }, [selectedOrg, setSelectedGraphId]);
  // If there is a single variant, select it
  React.useEffect(() => {
    if (selectedGraph?.variants?.length === 1) {
      setSelectedVariantId(selectedGraph.variants[0]?.id ?? '');
    }
  }, [selectedGraph, setSelectedVariantId]);

  const sandboxSelected = values.selectedOrgId === SANDBOX_ID;

  const disabledOrgItemChecker = React.useCallback(() => undefined, []);
  const disabledGraphItemChecker = React.useCallback(
    () => ({
      isDisabled: sandboxSelected,
      disabledExplanation: 'Select an org to duplicate to a graph',
    }),
    [sandboxSelected],
  );
  const disabledVariantItemChecker = React.useCallback(
    () => ({
      isDisabled: sandboxSelected,
      disabledExplanation: 'Select an org to duplicate to a variant',
    }),
    [sandboxSelected],
  );

  const hasDestinationError = !!(
    (errors.selectedOrgId && touched.selectedOrgId) ||
    (errors.selectedGraphId && touched.selectedGraphId) ||
    (errors.selectedVariantId && touched.selectedVariantId)
  );

  const shouldRedirectAfterSubmit = sandboxSelected
    ? !isSandboxRoute
    : !!values.selectedVariantId &&
      graphRefToString(currentGraphRef) !== values.selectedVariantId;

  const additionalListItemsByKey = React.useMemo(
    () => ({
      [SANDBOX_ID]: (props: React.ComponentProps<typeof ListItem>) => (
        <ListItem
          as={<div />}
          {...props}
          onClick={(e) => {
            props.onClick?.(e);
          }}
        >
          <div className="flex items-center">
            <IconSandbox className="mr-2 size-5" />
            <div
              className={classnames(
                'truncate text-base',
                props.selected && 'font-semibold',
              )}
            >
              Sandbox
            </div>
          </div>
        </ListItem>
      ),
    }),
    [],
  );

  const errorEntries = Object.values(errors);

  return (
    <Modal isOpen={isOpen} onClose={() => onClose()} size="xl">
      <ModalOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate={true}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Duplicate Collection</ModalTitle>
            <ModalDescription>
              This does not keep the two copies in sync.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading || collectionNameValidationLoading ? (
              <Loading />
            ) : !collection || !orgs ? (
              <div>Could not fetch details for collection</div>
            ) : (
              <>
                <FormControl
                  isRequired
                  isInvalid={!!errors.name && !!touched.name}
                >
                  <FormLabel>Collection Name</FormLabel>
                  <Input
                    isDisabled={isSubmitting}
                    autoFocus
                    name="name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={`Your ${
                      collection.isShared ? 'Shared' : 'Personal'
                    } Collection`}
                    value={values.name}
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasDestinationError}>
                  <FormLabel>Destination</FormLabel>
                  <div
                    className={classnames(
                      'flex items-center rounded border p-1',
                      hasDestinationError
                        ? 'border-red'
                        : 'border-silver-darker',
                    )}
                  >
                    <MultiOrgSelect
                      selectedOrgId={selectedOrg?.id || null}
                      setSelectedOrgId={setSelectedOrgId}
                      orgs={orgs}
                      triggerContent={
                        <div className="flex items-center">
                          {selectedOrg ? (
                            <Avatar
                              name={selectedOrg.name}
                              src={selectedOrg.avatarUrl ?? undefined}
                              size="xs"
                              variant="org"
                            />
                          ) : sandboxSelected ? (
                            <IconSandbox className="size-5 flex-shrink-0" />
                          ) : (
                            <div className="size-5 rounded bg-silver-darker" />
                          )}
                        </div>
                      }
                      disabledItemChecker={disabledOrgItemChecker}
                      additionalListItemsByKey={additionalListItemsByKey}
                      onBlur={handleBlur}
                    />
                    <Tooltip
                      label="Sandbox collections cannot have a variant"
                      disabled={!sandboxSelected}
                    >
                      <div className="flex items-center">
                        <GraphSelect
                          triggerText={selectedGraph?.title || 'select a graph'}
                          selectedGraphId={selectedGraph?.id ?? null}
                          setSelectedGraphId={setSelectedGraphId}
                          graphs={graphs}
                          disableTrigger={!selectedOrg}
                          disabledItemChecker={disabledGraphItemChecker}
                          onBlur={handleBlur}
                        />
                        <div className="mb-1 font-semibold text-secondary">
                          @
                        </div>
                        <VariantSelect
                          triggerText={
                            selectedVariant?.name || 'select a variant'
                          }
                          selectedVariantId={selectedVariant?.id ?? null}
                          setSelectedVariantId={setSelectedVariantId}
                          variants={variants}
                          disableTrigger={!selectedGraph}
                          disabledItemChecker={disabledVariantItemChecker}
                          onBlur={handleBlur}
                        />
                      </div>
                    </Tooltip>
                    <Tooltip
                      label="Sandbox collections can only be personal"
                      disabled={!sandboxSelected}
                    >
                      <div className="flex items-center">
                        <IconBookmark className="size-4 text-icon-secondary" />
                        <Select
                          disabled={!selectedVariant}
                          feel="flat"
                          size="standard"
                          className="max-w-44 truncate border-none"
                          listAs={
                            <div className="max-h-72 w-72 outline-none" />
                          }
                          onChange={(e) =>
                            setIsShared(e.target.value === 'Shared')
                          }
                          onBlur={handleBlur}
                          value={values.isShared ? 'Shared' : 'Personal'}
                          renderListItem={(itemProps, optionElement) => {
                            const isDisabled = optionElement.props.disabled;

                            return (
                              <ListItem
                                {...itemProps}
                                interactive={!isDisabled}
                                className={classnames(
                                  // eslint-disable-next-line local/no-conditional-class-names
                                  itemProps.className,
                                  isDisabled && 'opacity-30',
                                )}
                              >
                                <Tooltip
                                  label="You do not have permission to share collection in this variant"
                                  disabled={!isDisabled}
                                >
                                  <div>{optionElement}</div>
                                </Tooltip>
                              </ListItem>
                            );
                          }}
                        >
                          <option value="Personal">Personal</option>
                          <option
                            value="Shared"
                            disabled={
                              !selectedVariant?.permissions
                                .canShareCollectionInVariant
                            }
                          >
                            Shared
                          </option>
                        </Select>
                      </div>
                    </Tooltip>
                  </div>
                  <FormErrorMessage>
                    {[
                      errors.selectedOrgId,
                      errors.selectedGraphId,
                      errors.selectedVariantId,
                    ]
                      .filter(
                        (message): message is string => message !== undefined,
                      )
                      .map((message, i) => (
                        <React.Fragment key={message}>
                          {i !== 0 && <br />}
                          {message}
                        </React.Fragment>
                      ))}
                  </FormErrorMessage>
                </FormControl>
                {values.isShared && (
                  <Alert
                    status="info"
                    data-testid="shared_collection_permissions"
                  >
                    <span className="font-semibold">All</span> users with access
                    to {values.selectedVariantId} can view and edit this
                    collection and its operations. You can configure the
                    permissions once the copy is created.
                  </Alert>
                )}
                {globalErrorMessage && (
                  <ErrorMessage>{globalErrorMessage}</ErrorMessage>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={isSubmitting}
                onClick={() => onClose()}
                type="button"
              >
                Cancel
              </Button>
              <Tooltip
                disabled={
                  errorEntries.length === 0 && !shouldRedirectAfterSubmit
                }
                label={
                  errorEntries.length
                    ? errorEntries.flatMap((error, i) => (
                        <>
                          {i !== 0 && <br />}
                          {error}
                        </>
                      ))
                    : `Duplicate and open ${
                        values.selectedOrgId === SANDBOX_ID
                          ? 'Sandbox'
                          : values.selectedVariantId
                      } in new tab`
                }
              >
                <Button
                  variant="primary"
                  isDisabled={
                    collectionNameValidationLoading ||
                    !collection ||
                    !orgs ||
                    Object.entries(errors).length !== 0
                  }
                  isLoading={isSubmitting}
                  loadingText="Duplicate"
                  type="submit"
                  rightIcon={
                    shouldRedirectAfterSubmit ? (
                      <IconOutlink className="size-3" />
                    ) : undefined
                  }
                >
                  Duplicate
                </Button>
              </Tooltip>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </CustomForm>
    </Modal>
  );
};
